/* TODO: t() */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { Component } from 'react';
import { View, StyleSheet } from 'react-native';

import { Checkbox, Text, TextInput } from '../../../uiold';
import * as Constants from '../../../uiold/Constants';
import Infotip from '../../../uiold/Infotip';
import {
  APP_FIELDS,
  APP_FIELDS_BY_ID,
  APP_FIELDS_CAN_HIDE,
} from '../../../../support/shared/constants/Constants';
import shallowEqual from '../../../../support/helpers/shallowEqual';
import FormBuilderActions from '../../../../support/FormBuilderActions';
import hasOwnProperty from '../../../../support/helpers/hasOwnProperty';

import { FormField } from '../types/FormField';
import { Addon } from '../../../../types/shared/Addon';

type Props = {
  parent: FormField;
  component: React.ComponentType<any>;
  beforeDataUpdate?: (data: {
    [key: string]: any;
  }) => {
    [key: string]: any;
  };
  addons?: Map<string, Addon> | null | undefined;
};

type State = {
  isSystemField: boolean;
  // These are copied from props.parent.definition
  name: string;
  label: string;
  description: string;
  hidden: boolean;
};

const { themeColors } = Constants;
const STATE_KEYS = ['isSystemField', 'name', 'label', 'hidden'];

class FormFieldEditor extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    let { definition } = props.parent;
    let name = typeof definition.name === 'string' ? definition.name : '';
    let label = typeof definition.label === 'string' ? definition.label : '';
    let hidden =
      typeof definition.hidden === 'boolean' ? definition.hidden : false;
    this.state = {
      isSystemField: this._isSystemField(),
      name,
      label,
      description: label,
      hidden,
    };
  }

  shouldComponentUpdate(newProps: Props, newState: State) {
    if (!shallowEqual(this.props, newProps)) {
      return true;
    }
    if (!shallowEqual(this.state, newState)) {
      return true;
    }
    return false;
  }

  render() {
    return (
      <View>
        {this.state.hidden ? this._renderHidden() : this._renderChild()}
        {this.props.parent.definition.id !== APP_FIELDS.ID_FIELD_EMAIL &&
          this._renderHideOption()}
      </View>
    );
  }

  _renderChild = () => {
    let { component: ChildComponent, ...props } = this.props;
    let { definition } = this.props.parent;
    return (
      <ChildComponent
        {...props}
        data={definition}
        updateValue={this._updateValue}
        updateValues={this._updateValues}
        renderNameInput={this._renderNameInput}
        renderLabelInput={this._renderLabelInput}
      />
    );
  };

  _renderHidden = () => {
    return (
      <View>
        <Text>
          This field is hidden. It will not be shown in the public form and it
          will not be shown in the vendor profile.
        </Text>
      </View>
    );
  };

  _renderNameInput = () => {
    let { name } = this.state;
    let inputStyle: ObjectOf<any> = {};
    let warningText = '';
    if (name.trim() && name.length > 20) {
      inputStyle.borderColor = themeColors.warningBorderYellow;
      warningText = 'Please enter a shorter name.';
    } else if (name.trim().length === 0) {
      inputStyle.borderColor = themeColors.warningBorderRed;
      warningText = 'Please enter a name.';
    }

    return (
      <View style={{ zIndex: 3 }} accessibilityRole="label">
        <View style={styles.textInput}>
          <Text style={styles.label}>Short Name</Text>
          <Infotip
            width={150}
            text="Name of the field when exporting the data. This should be no more than 20 characters long."
          />
        </View>
        <TextInput
          value={this.state.name}
          onBlur={(_value) => {
            this._updateValueFromEvent('name', this.state.name);
          }}
          onChangeText={(value) => {
            this.setState({ name: value });
          }}
          style={inputStyle}
        />
        {!!warningText && (
          <Text style={{ color: themeColors.red }}>{warningText}</Text>
        )}
      </View>
    );
  };

  _renderLabelInput = () => {
    return (
      <View style={{ zIndex: 2 }} accessibilityRole="label">
        <View style={styles.textInput}>
          <Text style={styles.label}>Description</Text>
          <Infotip
            width={150}
            text="Description shown to the person filling out the form"
          />
        </View>
        <TextInput
          value={this.state.description}
          onChangeText={(value) => {
            this._onDescriptionFieldChange(value);
          }}
          onBlur={() => this._onDescriptionFieldBlur('label')}
        />
      </View>
    );
  };

  _renderHideOption = () => {
    let { hidden } = this.state;
    // only allow system fields to be hidden (since they can't be deleted)
    // Inlcuding addon fields because they shouldn't be deleted
    if (
      !this._isSystemField() &&
      !(this.props.parent.definition.id in APP_FIELDS_CAN_HIDE) &&
      !(this.props.parent.definition.type === 'AddonElement')
    ) {
      return;
    }
    return (
      <Checkbox
        text="Hide this field"
        isChecked={!!hidden}
        onChange={(value) => this._onHideFieldChange(!!value)}
      />
    );
  };

  _isSystemField = () => {
    return this.props.parent.id in APP_FIELDS_BY_ID;
  };

  _onDescriptionFieldBlur = (key: string) => {
    let { label, name } = this.state;
    if (label.trim()) {
      this._updateValue(key, label);
    } else {
      this.setState({ description: name });
      this._updateValue(key, name);
    }
  };

  _onDescriptionFieldChange = (value: string) => {
    this.setState({ description: value, label: value });
  };

  _onHideFieldChange = (value: boolean) => {
    this.setState({ hidden: !value });
    this._updateValue('hidden', value);
  };

  _updateValueFromEvent = (key: string, value: string) => {
    this._updateValue(key, value);
  };

  _updateValue = (key: string, newValue: unknown) => {
    this._updateValues({
      [key]: newValue,
    });
  };

  _updateValues = (updates: Partial<State>) => {
    updates = updates.label ? this._updateLabel(updates) : updates;
    let { beforeDataUpdate } = this.props;
    if (beforeDataUpdate) {
      updates = beforeDataUpdate(updates);
    }
    this.setState(getStateFromData(updates as State));
    FormBuilderActions.updateField(this.props.parent, updates);
  };

  _updateLabel = (updates: { [key: string]: unknown }) => {
    if (updates.name != null) {
      updates = Object.assign({}, updates, { label: updates.name });
    }
    return updates;
  };
}

function getStateFromData<T extends ObjectOf<any>>(data: T): T {
  let state: ObjectOf<any> = {};
  for (let key of STATE_KEYS) {
    if (hasOwnProperty(data, key)) {
      state[key] = data[key];
    }
  }
  return state as T;
}

let styles = StyleSheet.create({
  textInput: {
    flexDirection: 'row',
    marginVertical: 5,
    zIndex: 1,
  },
  label: {
    fontWeight: 'normal',
  },
});

export default FormFieldEditor;
